import { useCallback, useEffect, useRef, useState } from 'react';
import { synthesizeLive } from '../services/lectureTtsApi';

// Reads arbitrary text aloud via POST /tts/live. Handles the single shared
// <audio> element, object-URL cleanup, and a loading/playing state for the UI.
// Used by the Explain and Ask panels' "Read aloud" controls.
export function useReadAloud() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'playing'>('idle');

  const cleanup = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    cleanup();
    setState('idle');
  }, [cleanup]);

  const read = useCallback(
    async (text: string, voice?: string) => {
      if (!text.trim()) return;
      // Toggle off if already speaking.
      if (state !== 'idle') {
        stop();
        return;
      }
      setState('loading');
      try {
        const { url, isObjectUrl } = await synthesizeLive(text, voice);
        if (isObjectUrl) objectUrlRef.current = url;
        const el = new Audio(url);
        audioRef.current = el;
        el.onended = () => stop();
        el.onerror = () => stop();
        await el.play();
        setState('playing');
      } catch {
        stop();
      }
    },
    [state, stop]
  );

  // Revoke any object URL / stop audio on unmount.
  useEffect(() => cleanup, [cleanup]);

  return { read, stop, state };
}
